// src/store/slices/assessmentSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { assessment } from "../../types/index";

// Initial state for the assessment slice
const initialState: assessment.AssessmentStateType = {
  assessments: {
    isLoading: false,
    data: null,
    errors: null,
  },
  assessmentDisease: {
    isLoading: false,
    data: null,
    errors: null,
  },
  assessmentAISummary: {
    isLoading: false,
    data: [],
    errors: null,
  },
  aiAssessmentsDone: {
    isLoading: false,
    data: [],
    errors: null,
  },
  dietPlanData: {
    isLoading: false,
    data: null,
    errors: null,
  },
};

// Define the assessment slice
const assessmentSlice = createSlice({
  name: "assessment",
  initialState,
  reducers: {
    getAssessmentDiseaseRequest: (
      state: assessment.AssessmentStateType,
      action: PayloadAction<any>
    ) => {
      state.assessmentDisease.isLoading = true;
      state.assessmentDisease.data = null;
      state.assessmentDisease.errors = "";
    },
    getAssessmentDiseaseSuccess: (
      state: assessment.AssessmentStateType,
      action: PayloadAction<any>
    ) => {
      state.assessmentDisease.isLoading = false;
      state.assessmentDisease.data = action.payload.data;
      state.assessmentDisease.errors = null;
    },
    getAssessmentDiseaseError: (
      state: assessment.AssessmentStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.assessmentDisease.isLoading = false;
      state.assessmentDisease.data = null;
      state.assessmentDisease.errors = error;
    },
    resetAssessmentDisease: (
      state: assessment.AssessmentStateType,
    ) => {
      state.assessmentDisease.isLoading = false;
      state.assessmentDisease.data = null;
      state.assessmentDisease.errors = null;
    },

    getAssessmentsRequest: (
      state: assessment.AssessmentStateType,
      action: PayloadAction<any>
    ) => {
      state.assessments.isLoading = true;
      state.assessments.data = null;
      state.assessments.errors = "";
    },
    getAssessmentsSuccess: (
      state: assessment.AssessmentStateType,
      action: PayloadAction<any>
    ) => {
      state.assessments.isLoading = false;
      state.assessments.data = action.payload.data;
      state.assessments.errors = null;
    },
    getAssessmentsError: (
      state: assessment.AssessmentStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.assessments.isLoading = false;
      state.assessments.data = null;
      state.assessments.errors = error;
    },
    resetAssessments: (
      state: assessment.AssessmentStateType,
    ) => {
      state.assessments.isLoading = false;
      state.assessments.data = null;
      state.assessments.errors = null;
    },

    getAssessmentAISummaryRequest: (
      state: assessment.AssessmentStateType,
      action: PayloadAction<any>
    ) => {
      state.assessmentAISummary.isLoading = true;
      state.assessmentAISummary.data = [];
      state.assessmentAISummary.errors = "";
    },
    getAssessmentAISummarySuccess: (
      state: assessment.AssessmentStateType,
      action: PayloadAction<any>
    ) => {
      state.assessmentAISummary.isLoading = false;
      state.assessmentAISummary.data = action.payload?.data || [];
      state.assessmentAISummary.errors = null;
    },
    getAssessmentAISummaryError: (
      state: assessment.AssessmentStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.assessmentAISummary.isLoading = false;
      state.assessmentAISummary.data = [];
      state.assessmentAISummary.errors = error;
    },
    resetAssessmentAISummary: (
      state: assessment.AssessmentStateType,
    ) => {
      state.assessmentAISummary.isLoading = false;
      state.assessmentAISummary.data = [];
      state.assessmentAISummary.errors = null;
    },
    getAIAssessmentsDoneRequest: (
      state: assessment.AssessmentStateType,
      action: PayloadAction<any>
    ) => {
      state.aiAssessmentsDone.isLoading = true;
      state.aiAssessmentsDone.data = [];
      state.aiAssessmentsDone.errors = "";
    },
    getAIAssessmentsDoneSuccess: (
      state: assessment.AssessmentStateType,
      action: PayloadAction<any>
    ) => {
      state.aiAssessmentsDone.isLoading = false;
      state.aiAssessmentsDone.data = action.payload?.data || [];
      state.aiAssessmentsDone.errors = null;
    },
    getAIAssessmentsDoneError: (
      state: assessment.AssessmentStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.aiAssessmentsDone.isLoading = false;
      state.aiAssessmentsDone.data = [];
      state.aiAssessmentsDone.errors = error;
    },
    resetAIAssessmentsDone: (
      state: assessment.AssessmentStateType,
    ) => {
      state.aiAssessmentsDone.isLoading = false;
      state.aiAssessmentsDone.data = [];
      state.aiAssessmentsDone.errors = null;
    },
    getDietPlanDataRequest: (
      state: assessment.AssessmentStateType,
      action: PayloadAction<any>
    ) => {
      state.dietPlanData.isLoading = true;
      state.dietPlanData.data = null;
      state.dietPlanData.errors = "";
    },
    getDietPlanDataSuccess: (
      state: assessment.AssessmentStateType,
      action: PayloadAction<any>
    ) => {
      state.dietPlanData.isLoading = false;
      state.dietPlanData.data = action.payload?.data || null;
      state.dietPlanData.errors = null;
    },
    getDietPlanDataError: (
      state: assessment.AssessmentStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.dietPlanData.isLoading = false;
      state.dietPlanData.data = null;
      state.dietPlanData.errors = error;
    },
    resetDietPlanData: (
      state: assessment.AssessmentStateType,
    ) => {
      state.dietPlanData.isLoading = false;
      state.dietPlanData.data = null;
      state.dietPlanData.errors = null;
    },
  },
});

// Export actions and reducer
export const {
  getAssessmentDiseaseRequest,
  getAssessmentDiseaseSuccess,
  getAssessmentDiseaseError,
  resetAssessmentDisease,

  getAssessmentsRequest,
  getAssessmentsSuccess,
  getAssessmentsError,
  resetAssessments,

  getAssessmentAISummaryRequest,
  getAssessmentAISummarySuccess,
  getAssessmentAISummaryError,
  resetAssessmentAISummary,

  getAIAssessmentsDoneRequest,
  getAIAssessmentsDoneSuccess,
  getAIAssessmentsDoneError,
  resetAIAssessmentsDone,

  getDietPlanDataRequest,
  getDietPlanDataSuccess,
  getDietPlanDataError,
  resetDietPlanData,
} = assessmentSlice.actions;

export default assessmentSlice.reducer;
